import { TextField } from "@mui/material";
import lodash from "lodash";
import * as React from "react";

import {
  CAMERA_TYPE,
  type ThirdPersonCamera,
} from "@/shared/state/visualization/schema/v1/three-d";

import styles from "./SceneSettings.module.css";

/**
 * Angles are stored in radians and shown in degrees.
 *
 * The schema is radians because that is what Babylon's `ArcRotateCamera` takes;
 * a person setting a viewpoint thinks in degrees. Rounded on the way out — a
 * 70° default round-trips to `70.00000000000001` otherwise, which is a nonsense
 * thing to put in a text box.
 */
const RADIANS_PER_DEGREE = Math.PI / 180;

function toDegrees(radians: number): number {
  return Math.round((radians / RADIANS_PER_DEGREE) * 1e6) / 1e6;
}

/**
 * Beta's open interval, in degrees.
 *
 * Babylon clamps beta to `[0.01, π - 0.01]` at runtime, so a value outside this
 * is not rejected so much as quietly ignored — which is worse than being told.
 */
const MIN_ELEVATION_DEG = 0;
const MAX_ELEVATION_DEG = 180;

interface ThirdPersonCameraFieldsProps {
  /** The third-person camera as it currently stands. */
  value: ThirdPersonCamera;
  /**
   * Called with the edited camera whenever the inputs parse to something
   * different from {@link ThirdPersonCameraFieldsProps.value}.
   *
   * Should be stable across renders; see {@link GridFields}.
   */
  onChange: (camera: ThirdPersonCamera) => void;
}

/**
 * The third-person camera's focus point, angles and distance, as plain inputs.
 *
 * A third-person camera frames a **free-floating point** — there is no tracked
 * entity and no zoom envelope in its spec, so it has none of the fields
 * {@link OrbitCameraFields} edits. The two are separate components rather than
 * one component with optional fields precisely so that neither can write the
 * other's shape: `camera` is a discriminated union, and an orbit `target` landing
 * on a third-person spec is a schema violation that no amount of downstream
 * defaulting recovers from.
 *
 * `radius` is a *request*, not a guarantee. `constrainCamera` clamps the live
 * camera into a scene-derived envelope every frame, so a distance far inside or
 * outside the scene's own scale is honoured only as far as that envelope allows.
 */
export function ThirdPersonCameraFields({
  value,
  onChange,
}: ThirdPersonCameraFieldsProps) {
  const [focusX, setFocusX] = React.useState(`${value.focusPoint.x}`);
  const [focusY, setFocusY] = React.useState(`${value.focusPoint.y}`);
  const [focusZ, setFocusZ] = React.useState(`${value.focusPoint.z}`);
  const [alpha, setAlpha] = React.useState(`${toDegrees(value.alpha)}`);
  const [beta, setBeta] = React.useState(`${toDegrees(value.beta)}`);
  const [radius, setRadius] = React.useState(`${value.radius}`);

  const [focusError, setFocusError] = React.useState("");
  const [alphaError, setAlphaError] = React.useState("");
  const [betaError, setBetaError] = React.useState("");
  const [radiusError, setRadiusError] = React.useState("");

  React.useEffect(
    function onSettingChanged() {
      setFocusError("");
      const parsedFocus = [focusX, focusY, focusZ].map(finiteOrUndefined);
      if (parsedFocus.some((coordinate) => coordinate === undefined)) {
        setFocusError("Focus point coordinates must be numbers");
        return;
      }
      const [x, y, z] = parsedFocus as [number, number, number];

      setAlphaError("");
      const parsedAlpha = finiteOrUndefined(alpha);
      if (parsedAlpha === undefined) {
        setAlphaError("Rotation must be a number");
        return;
      }

      setBetaError("");
      const parsedBeta = finiteOrUndefined(beta);
      if (parsedBeta === undefined) {
        setBetaError("Elevation must be a number");
        return;
      } else if (
        parsedBeta <= MIN_ELEVATION_DEG ||
        parsedBeta >= MAX_ELEVATION_DEG
      ) {
        setBetaError(
          `Elevation must be between ${MIN_ELEVATION_DEG} and ${MAX_ELEVATION_DEG} degrees`,
        );
        return;
      }

      setRadiusError("");
      const parsedRadius = finiteOrUndefined(radius);
      if (parsedRadius === undefined) {
        setRadiusError("Distance must be a number");
        return;
      } else if (parsedRadius <= 0) {
        setRadiusError("Distance must be greater than zero");
        return;
      }

      const updatedCamera: ThirdPersonCamera = {
        type: CAMERA_TYPE.thirdPerson,
        focusPoint: { x, y, z },
        alpha: parsedAlpha * RADIANS_PER_DEGREE,
        beta: parsedBeta * RADIANS_PER_DEGREE,
        radius: parsedRadius,
      };

      if (!lodash.isEqual(value, updatedCamera)) {
        onChange(updatedCamera);
      }
    },
    [focusX, focusY, focusZ, alpha, beta, radius, value, onChange],
  );

  return (
    <div className={styles.container}>
      <div className={`${styles.inputContainer} ${styles.field}`}>
        <TextField
          label="Focus X"
          autoComplete="off"
          type="number"
          size="small"
          value={focusX}
          onChange={(e) => setFocusX(e.target.value)}
          error={!!focusError}
          className={styles.input}
          InputLabelProps={{ className: styles.input }}
          inputProps={{ className: styles.input }}
        />
        <div className={styles.spacer} />
        <TextField
          label="Focus Y"
          autoComplete="off"
          type="number"
          size="small"
          value={focusY}
          onChange={(e) => setFocusY(e.target.value)}
          error={!!focusError}
          className={styles.input}
          InputLabelProps={{ className: styles.input }}
          inputProps={{ className: styles.input }}
        />
        <div className={styles.spacer} />
        <TextField
          label="Focus Z"
          autoComplete="off"
          type="number"
          size="small"
          value={focusZ}
          onChange={(e) => setFocusZ(e.target.value)}
          error={!!focusError}
          helperText={focusError}
          className={styles.input}
          InputLabelProps={{ className: styles.input }}
          inputProps={{ className: styles.input }}
        />
      </div>
      <div className={`${styles.inputContainer} ${styles.field}`}>
        <TextField
          label="Rotation (°)"
          autoComplete="off"
          type="number"
          size="small"
          value={alpha}
          onChange={(e) => setAlpha(e.target.value)}
          error={!!alphaError}
          helperText={alphaError}
          className={styles.input}
          InputLabelProps={{ className: styles.input }}
          inputProps={{ className: styles.input }}
        />
        <div className={styles.spacer} />
        <TextField
          label="Elevation (°)"
          autoComplete="off"
          type="number"
          size="small"
          value={beta}
          onChange={(e) => setBeta(e.target.value)}
          error={!!betaError}
          helperText={betaError}
          className={styles.input}
          InputLabelProps={{ className: styles.input }}
          inputProps={{ className: styles.input }}
        />
      </div>
      <TextField
        label="Distance"
        autoComplete="off"
        type="number"
        size="small"
        fullWidth
        value={radius}
        onChange={(e) => setRadius(e.target.value)}
        error={!!radiusError}
        helperText={radiusError}
        className={styles.field}
        InputLabelProps={{ className: styles.input }}
        InputProps={{ className: styles.input }}
        inputProps={{ className: styles.input }}
      />
    </div>
  );
}

/** The number a field holds, or `undefined` if it does not hold one. */
function finiteOrUndefined(text: string): number | undefined {
  if (text.trim() === "") {
    return undefined;
  }
  const parsed = Number.parseFloat(text);
  return Number.isFinite(parsed) ? parsed : undefined;
}
